import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Alert, Chip, TablePagination, Tooltip, MenuItem,
  Select, FormControl, InputLabel, OutlinedInput, Checkbox, ListItemText,
} from '@mui/material';
import { invoiceApi, feeTypeApi } from '../../services/api';
import { Invoice, FeeType } from '../../types';

const statusColors: Record<string, 'success' | 'error' | 'info' | 'warning' | 'default'> = {
  Paid: 'success', Overdue: 'error', Sent: 'info', Partial: 'warning', Cancelled: 'default',
};

const InvoiceList: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [feeTypes, setFeeTypes] = useState<FeeType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Generate dialog state
  const [genOpen, setGenOpen] = useState(false);
  const [genFeeTypeIds, setGenFeeTypeIds] = useState<number[]>([]);
  const [genInvoiceDate, setGenInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [genDueDate, setGenDueDate] = useState('');
  const [genAmount, setGenAmount] = useState('');
  const [genSaving, setGenSaving] = useState(false);
  const [genError, setGenError] = useState('');
  const [genResult, setGenResult] = useState('');

  const hasVariableFee = feeTypes.some(ft => genFeeTypeIds.includes(ft.id) && ft.isVariableAmount);
  const selectedFeeTypes = feeTypes.filter(ft => genFeeTypeIds.includes(ft.id));

  const fetchInvoices = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [invRes, ftRes] = await Promise.all([invoiceApi.getAll(), feeTypeApi.getAll()]);
      setInvoices(Array.isArray(invRes.data) ? invRes.data : invRes.data?.data || []);
      setFeeTypes(Array.isArray(ftRes.data) ? ftRes.data : ftRes.data?.data || []);
    } catch { setError('Failed to load invoices.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const openGenerateDialog = () => {
    setGenFeeTypeIds([]);
    setGenInvoiceDate(new Date().toISOString().split('T')[0]);
    const due = new Date(); due.setDate(due.getDate() + 30);
    setGenDueDate(due.toISOString().split('T')[0]);
    setGenAmount(''); setGenError(''); setGenResult('');
    setGenOpen(true);
  };

  const handleGenerate = async () => {
    if (genFeeTypeIds.length === 0) { setGenError('Select at least one fee type.'); return; }
    if (hasVariableFee && (!genAmount || Number(genAmount) <= 0)) { setGenError('Amount is required for variable fee types.'); return; }

    setGenSaving(true); setGenError(''); setGenResult('');
    try {
      const payload: any = {
        feeTypeIds: genFeeTypeIds,
        invoiceDate: new Date(genInvoiceDate).toISOString(),
        dueDate: new Date(genDueDate).toISOString(),
      };
      if (hasVariableFee && genAmount) payload.amount = Number(genAmount);
      const res = await invoiceApi.generate(payload);
      setGenResult(res.data?.message || `Generated invoices.`);
      await fetchInvoices();
    } catch (err: any) { setGenError(err.response?.data?.message || 'Failed to generate invoices.'); }
    finally { setGenSaving(false); }
  };

  const displayedInvoices = invoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const formatCurrency = (value: number) => `RM${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Invoices</Typography>
        <Button variant="contained" onClick={openGenerateDialog} disabled={feeTypes.length === 0}>
          Generate Invoices
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Invoice #</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Owner</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Balance</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No invoices. Click "Generate Invoices" to create one.</Typography>
                  </TableCell>
                </TableRow>
              ) : displayedInvoices.map((invoice) => (
                <TableRow key={invoice.id} hover>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{invoice.invoiceNumber}</Typography></TableCell>
                  <TableCell>{invoice.unitNumber || `Unit #${invoice.unitId}`}</TableCell>
                  <TableCell>{invoice.ownerName || `Owner #${invoice.ownerId}`}</TableCell>
                  <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                  <TableCell>
                    <Tooltip title={new Date(invoice.dueDate) < new Date() && invoice.status !== 'Paid' ? 'Overdue' : ''}>
                      <span>{formatDate(invoice.dueDate)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{formatCurrency(invoice.totalAmount)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600, color: invoice.balanceDue > 0 ? 'error.main' : 'success.main' }}>
                      {formatCurrency(invoice.balanceDue)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={invoice.status} color={statusColors[invoice.status] || 'default'} size="small"
                      variant={invoice.status === 'Paid' ? 'filled' : 'outlined'} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={invoices.length}
            rowsPerPage={rowsPerPage} page={page} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </TableContainer>
      )}

      <Dialog open={genOpen} onClose={() => !genSaving && setGenOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Generate Invoices</DialogTitle>
        <DialogContent>
          {genError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{genError}</Alert>}
          {genResult && <Alert severity="success" sx={{ mb: 2, mt: 1 }}>{genResult}</Alert>}

          <FormControl fullWidth margin="normal">
            <InputLabel>Fee Types *</InputLabel>
            <Select
              multiple
              value={genFeeTypeIds}
              onChange={e => setGenFeeTypeIds(e.target.value as number[])}
              input={<OutlinedInput label="Fee Types *" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(id => {
                    const ft = feeTypes.find(f => f.id === id);
                    return ft ? <Chip key={id} label={ft.name} size="small" /> : null;
                  })}
                </Box>
              )}
              disabled={genSaving}
            >
              {feeTypes.filter(f => f.isActive).map(f => (
                <MenuItem key={f.id} value={f.id}>
                  <Checkbox checked={genFeeTypeIds.includes(f.id)} />
                  <ListItemText primary={f.name} secondary={
                    f.isPerSqFt ? `RM${f.amount.toFixed(2)}/sqft` :
                    f.isVariableAmount ? 'Variable' :
                    f.percentageRate > 0 ? `${f.percentageRate}% of parent` :
                    `RM${f.amount.toFixed(2)}`
                  } />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Show summary of selected fee types */}
          {selectedFeeTypes.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {selectedFeeTypes.map(ft => (
                <Box key={ft.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    • {ft.name}:
                  </Typography>
                  {ft.isPerSqFt ? (
                    <Chip label="Per Sq.Ft." size="small" color="info" />
                  ) : ft.isVariableAmount ? (
                    <Chip label="Variable" size="small" color="secondary" />
                  ) : ft.percentageRate > 0 ? (
                    <Chip label={`${ft.percentageRate}% of parent`} size="small" color="success" />
                  ) : ft.isRefundable ? (
                    <Chip label={`RM${ft.amount.toFixed(2)} (Refundable)`} size="small" color="warning" />
                  ) : (
                    <Chip label={`RM${ft.amount.toFixed(2)}`} size="small" />
                  )}
                </Box>
              ))}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                One invoice per unit will be generated with all selected fee types as line items.
              </Typography>
            </Box>
          )}

          {hasVariableFee && (
            <TextField label="Amount (RM)" fullWidth margin="normal" type="number"
              value={genAmount} onChange={e => setGenAmount(e.target.value)} required disabled={genSaving} />
          )}
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField label="Invoice Date" type="date" fullWidth margin="normal"
              value={genInvoiceDate} onChange={e => setGenInvoiceDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }} disabled={genSaving} />
            <TextField label="Due Date" type="date" fullWidth margin="normal"
              value={genDueDate} onChange={e => setGenDueDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }} disabled={genSaving} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenOpen(false)} disabled={genSaving}>Cancel</Button>
          <Button onClick={handleGenerate} variant="contained" disabled={genSaving}
            startIcon={genSaving ? <CircularProgress size={18} /> : undefined}>
            {genSaving ? 'Generating...' : `Generate (${genFeeTypeIds.length} fee type(s))`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceList;
